import React from "react";
import {
  ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip,
  BarChart, Bar, Legend, PieChart, Pie, Cell,
} from "recharts";
import {
  TrendingUp, MessageCircle, CalendarCheck, FileText, Download as DownloadIcon,
  Mail, Eye, Send, Activity,
} from "lucide-react";
import api from "@/lib/api";
import { Header } from "@/pages/admin/AdminBlog";

const eventMeta = {
  page_view: { label: "Page Views", icon: Eye, color: "#94A3B8" },
  lead_form_view: { label: "Lead Form Views", icon: Eye, color: "#0EA5E9" },
  lead_form_submit: { label: "Lead Submits", icon: Send, color: "#D4AF37" },
  open_account_click: { label: "Open Account Clicks", icon: TrendingUp, color: "#10B981" },
  download_app_click: { label: "Download App Clicks", icon: DownloadIcon, color: "#A78BFA" },
  whatsapp_click: { label: "WhatsApp Clicks", icon: MessageCircle, color: "#22D3EE" },
  calendly_click: { label: "Calendly Clicks", icon: CalendarCheck, color: "#F59E0B" },
  contact_form_submit: { label: "Contact Submits", icon: Mail, color: "#F472B6" },
};

const PIE_COLORS = ["#0EA5E9", "#D4AF37", "#10B981", "#A78BFA", "#F472B6", "#22D3EE", "#F59E0B"];

export default function AdminAnalytics() {
  const [data, setData] = React.useState(null);
  const [days, setDays] = React.useState(30);

  const load = React.useCallback(() => {
    api.get(`/admin/analytics?days=${days}`).then((r) => setData(r.data));
  }, [days]);

  React.useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-slate-400 text-sm">Loading analytics…</div>;

  const { totals = {}, daily = [], sources = [], funnel = {} } = data;

  return (
    <div data-testid="admin-analytics">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <Header title="Analytics" />
        <div className="flex items-center gap-2 bg-[#0B1221] border border-white/10 rounded-full p-1">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`analytics-range-${d}`}
              className={`px-3.5 py-1.5 text-xs rounded-full transition-colors ${
                days === d ? "bg-sky-500 text-white" : "text-slate-400 hover:text-white"
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* KPI cards */}
      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {Object.entries(eventMeta).slice(0, 8).map(([k, m]) => (
          <div key={k} className="card-luxury p-5" data-testid={`analytics-tile-${k}`}>
            <m.icon className="w-5 h-5" style={{ color: m.color }} />
            <div className="mt-3 font-[Outfit] text-3xl text-white font-light">{(totals[k] || 0).toLocaleString()}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">{m.label}</div>
          </div>
        ))}
      </div>

      {/* Funnel */}
      <div className="mt-8 card-luxury p-6">
        <div className="overline mb-3">Conversion Funnel</div>
        <h3 className="font-[Outfit] text-2xl text-white font-light">From visit to converted lead</h3>
        <div className="mt-6 grid sm:grid-cols-3 gap-4">
          <FunnelStep icon={Eye} label="Lead form views" value={funnel.lead_views} color="#0EA5E9" />
          <FunnelStep icon={Send} label="Lead submissions" value={funnel.lead_submits} color="#D4AF37" />
          <FunnelStep icon={Activity} label="Conversion rate" value={`${funnel.lead_conversion_pct}%`} color="#10B981" raw />
        </div>
      </div>

      {/* Daily chart */}
      <div className="mt-6 card-luxury p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="overline mb-1">Daily activity</div>
            <h3 className="font-[Outfit] text-xl text-white font-light">CTA events over the last {days} days</h3>
          </div>
          <div className="flex flex-wrap gap-2 text-[10px] uppercase tracking-widest">
            {["lead_form_submit", "whatsapp_click", "calendly_click", "open_account_click"].map((k) => (
              <span key={k} className="flex items-center gap-1.5 text-slate-400">
                <span className="w-2 h-2 rounded-full" style={{ background: eventMeta[k].color }} />
                {eventMeta[k].label}
              </span>
            ))}
          </div>
        </div>
        <div className="mt-4 h-[300px]" data-testid="analytics-daily-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={daily}>
              <defs>
                {["lead_form_submit", "whatsapp_click", "calendly_click", "open_account_click"].map((k) => (
                  <linearGradient key={k} id={`g-${k}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={eventMeta[k].color} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={eventMeta[k].color} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="date" stroke="#64748B" fontSize={10} />
              <YAxis stroke="#64748B" fontSize={10} allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: "#030712", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, fontSize: 12 }}
                labelStyle={{ color: "#94A3B8" }}
              />
              <Area type="monotone" dataKey="lead_form_submit" stroke={eventMeta.lead_form_submit.color} strokeWidth={2} fill="url(#g-lead_form_submit)" />
              <Area type="monotone" dataKey="whatsapp_click" stroke={eventMeta.whatsapp_click.color} strokeWidth={2} fill="url(#g-whatsapp_click)" />
              <Area type="monotone" dataKey="calendly_click" stroke={eventMeta.calendly_click.color} strokeWidth={2} fill="url(#g-calendly_click)" />
              <Area type="monotone" dataKey="open_account_click" stroke={eventMeta.open_account_click.color} strokeWidth={2} fill="url(#g-open_account_click)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Sources + breakdown */}
      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <div className="card-luxury p-6">
          <div className="overline mb-1">Lead Sources</div>
          <h3 className="font-[Outfit] text-xl text-white font-light">Where leads originate</h3>
          <div className="mt-4 h-[280px]" data-testid="analytics-sources-chart">
            {sources.length === 0 ? (
              <div className="flex items-center justify-center h-full text-slate-500 text-sm">No leads yet.</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={sources} dataKey="count" nameKey="source" innerRadius={60} outerRadius={100} stroke="none">
                    {sources.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#030712", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11, color: "#94A3B8" }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="card-luxury p-6">
          <div className="overline mb-1">Event totals</div>
          <h3 className="font-[Outfit] text-xl text-white font-light">All CTA actions ({days}d)</h3>
          <div className="mt-4 h-[280px]" data-testid="analytics-events-bar">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={Object.entries(eventMeta).map(([k, m]) => ({ name: m.label.replace(" Clicks", "").replace(" Submits", ""), count: totals[k] || 0, color: m.color }))} layout="vertical" margin={{ left: 0, right: 20 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" horizontal={false} />
                <XAxis type="number" stroke="#64748B" fontSize={10} />
                <YAxis dataKey="name" type="category" stroke="#94A3B8" fontSize={10} width={130} />
                <Tooltip contentStyle={{ background: "#030712", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, fontSize: 12 }} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                <Bar dataKey="count" radius={[0, 8, 8, 0]}>
                  {Object.entries(eventMeta).map(([k, m], i) => <Cell key={k} fill={m.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}

function FunnelStep({ icon: Icon, label, value, color, raw }) {
  return (
    <div className="rounded-2xl border border-white/5 bg-white/5 p-5">
      <Icon className="w-5 h-5" style={{ color }} />
      <div className="mt-4 font-[Outfit] text-4xl text-white font-light">
        {raw ? value : (value || 0).toLocaleString()}
      </div>
      <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">{label}</div>
    </div>
  );
}
